import React, { useState, useEffect } from 'react';
import accessLogsService from '../../../services/accessLogsService';
import LogTable from './LogTable';
import FilterPanel from './FilterPanel';

const FailedAttemptsTab = ({ darkMode }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 50,
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadData();
  }, [filters]);

  useEffect(() => {
    const handleRefresh = () => {
      loadData();
    };
    window.addEventListener('accessLogsRefresh', handleRefresh);
    return () => window.removeEventListener('accessLogsRefresh', handleRefresh);
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const logsData = await accessLogsService.getFailedAttempts(filters);
      setLogs(logsData.logs || []);
      setTotalCount(logsData.totalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to load failed attempts');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleSort = (column, order) => {
    setFilters({ ...filters, sortBy: column, sortOrder: order, page: 1 });
  };

  const handleBlockIP = async (ipAddress) => {
    if (!window.confirm(`Block IP address ${ipAddress}?`)) return;
    try {
      await accessLogsService.blockIP(ipAddress, 'Blocked from Failed Attempts tab', null);
      alert('IP address blocked successfully');
      loadData();
    } catch (err) {
      alert(`Failed to block IP: ${err.message}`);
    }
  };

  const columns = [
    { key: 'timestamp', label: 'Timestamp', sortable: true },
    { key: 'email', label: 'Attempted Email', sortable: false },
    { key: 'ipAddress', label: 'IP Address', sortable: true },
    { key: 'failureReason', label: 'Reason', sortable: false },
    { key: 'attemptCount', label: 'Attempts (24h)', sortable: false },
    { key: 'deviceInfo', label: 'Device', sortable: false },
    { key: 'actions', label: 'Actions', sortable: false }
  ];

  const formatLogRow = (log) => ({
    id: log.id,
    timestamp: new Date(log.timestamp).toLocaleString(),
    email: log.email || 'N/A',
    ipAddress: log.ipAddress || 'N/A',
    failureReason: log.failureReason || 'N/A',
    attemptCount: log.attemptCount || 0,
    deviceInfo: log.deviceInfo || 'N/A',
    isBlocked: log.isBlocked,
    severityLevel: log.severityLevel,
    actions: log.ipAddress ? (
      <button
        onClick={() => handleBlockIP(log.ipAddress)}
        className="block-ip-button"
        disabled={log.isBlocked}
      >
        {log.isBlocked ? 'Blocked' : 'Block IP'}
      </button>
    ) : null
  });

  return (
    <div className={`access-logs-tab-content ${darkMode ? 'dark-mode' : ''}`}>
      <FilterPanel filters={filters} onChange={handleFilterChange} darkMode={darkMode} />
      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadData}>Retry</button>
        </div>
      )}
      <LogTable
        columns={columns}
        data={logs.map(formatLogRow)}
        loading={loading}
        pagination={{
          currentPage: filters.page,
          pageSize: filters.pageSize,
          totalCount: totalCount,
          onPageChange: handlePageChange
        }}
        sorting={{
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          onSort: handleSort
        }}
        darkMode={darkMode}
        rowClassName={(row) => {
          if (row.attemptCount > 3) return 'high-attempts';
          if (row.severityLevel === 'critical') return 'critical';
          return '';
        }}
      />
    </div>
  );
};

export default FailedAttemptsTab;

